import React, {
  createContext,
  useEffect,
  ReactNode, useRef,
} from 'react';
import useCurrentDeviceId from '../hooks/useCurrentDeviceId';

const SOCKET_URL = process.env.SOCKET_URL || '';
const SOCKET_VERSION = process.env.SOCKET_VERSION || '';

export type SocketContextType = {
  close: () => void,
  isConnected: boolean,
  send: (payload: any) => void,
  subscribe: (channel: string, callback: (message: any) => void) => () => void;
};

const SocketContext = createContext<SocketContextType | null>(null);

export const SocketContextProvider = ({ children }: { children: ReactNode; }) => {
  const [isConnected, setIsConnected] = React.useState(false);
  const socketRef = useRef<WebSocket | null>(null);
  const subscribers = useRef<Map<string, Set<(message: any) => void>>>(new Map());
  const { currentDeviceId, currentDeviceType } = useCurrentDeviceId();

  useEffect(() => {
    if (currentDeviceId) {
      const newSocket = new WebSocket(`${SOCKET_URL}/${SOCKET_VERSION}`);
      newSocket.addEventListener('open', () => {
        newSocket.send(JSON.stringify({
          channel: 'authenticationRequest',
          metadata: {
            deviceType: currentDeviceType,
            version: 'v2.0',
            ID: 0
          },
          payload: {
            isConductor: true,
            deviceId: currentDeviceId,
            API_KEY: process.env.API_KEY,
          }
        }))
      });

      newSocket.addEventListener('message', (event) => {
        const message = JSON.parse(event.data);
        const channel = message.channel;

        if(!isConnected) {
          if (channel === 'authenticationResponse' && message?.payload?.authenticated) {
            setIsConnected(true);
          }
        }

        subscribers.current.get(channel)?.forEach((callback) => callback(message));
      });

      socketRef.current = newSocket

      return () => {
        newSocket.close();
        setIsConnected(false);
      };
    }
  }, [currentDeviceId]);

  const close = () => {
    socketRef?.current?.close();
  };

  const send = (payload: any) => {
    if (!socketRef.current || !isConnected) return;
    socketRef.current.send(payload);
  };

  const subscribe = (channel: string, callback: (message: any) => void) => {
    if (!subscribers.current.has(channel)) {
      subscribers.current.set(channel, new Set());
    }

    subscribers.current.get(channel)!.add(callback);

    return () => {
      subscribers.current.get(channel)?.delete(callback);

      if (subscribers.current.get(channel)?.size === 0) {
        subscribers.current.delete(channel);
      }
    };
  };

  return (
    <SocketContext.Provider
      value={{
        close,
        isConnected,
        send,
        subscribe,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
